import React, { cloneElement, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
  CalendarDays,
  MapPin,
  Building2,
  ArrowUpRight,
  X,
  Clock3,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";

export const CalendarActions = createContext(null);
export const calendarDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
export const EVENT_COLORS = {
  closed: "#37429c",
  delivery_in_progress: "#8953bd",
  shoot_completed: "#c87816",
  vip: "#d54757",
  high: "#b9770a",
  normal: "#5363b3",
};
const STATUS_LABELS = {
  event_created: "Created",
  event_scheduled: "Scheduled",
  shoot_completed: "Shoot complete",
  delivery_in_progress: "In delivery",
  closed: "Closed",
};

export function eventColor(event) {
  return (
    EVENT_COLORS[event.status] ||
    EVENT_COLORS[event.priority] ||
    EVENT_COLORS.normal
  );
}

export function CalendarEventLabel({ title }) {
  return (
    <span className="calendar-event-label">
      <span className="calendar-event-icon">
        <CalendarDays size={10} strokeWidth={2} />
      </span>
      <span>{title}</span>
    </span>
  );
}

export function CalendarDateHeader({ date, label }) {
  const { selectDate, selectedDate } = useContext(CalendarActions);
  return (
    <button
      type="button"
      className="calendar-date-number"
      aria-label={`Select ${date.toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}`}
      aria-pressed={calendarDateKey(date) === calendarDateKey(selectedDate)}
      onClick={() => selectDate(date)}
    >
      {Number(label)}
    </button>
  );
}

export function CalendarEventPopover({ event, children }) {
  const { selectDate, openDay, compact } = useContext(CalendarActions);
  const resource = event.resource;
  const start = new Date(resource.event_date_start);
  const end = resource.event_date_end
    ? new Date(resource.event_date_end)
    : null;
  const dateText = start.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const endText =
    end && calendarDateKey(end) !== calendarDateKey(start)
      ? ` – ${end.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
      : "";
  const timeText = start.toLocaleTimeString("en", {
    hour: "numeric",
    minute: "2-digit",
  });
  return (
    <Popover
      onOpenChange={(open) => {
        if (open) selectDate(event.start);
      }}
    >
      <PopoverTrigger asChild>
        {cloneElement(children, {
          role: "button",
          tabIndex: 0,
          "aria-label": `View event: ${event.title}`,
          onKeyDown: (keyEvent) => {
            if (keyEvent.key === "Enter" || keyEvent.key === " ") {
              keyEvent.preventDefault();
              keyEvent.currentTarget.click();
            }
          },
        })}
      </PopoverTrigger>
      <PopoverContent
        className="calendar-event-popover"
        side={compact ? "bottom" : "right"}
        align="start"
        sideOffset={12}
        collisionPadding={16}
        role="dialog"
        aria-label={event.title}
      >
        <PopoverPrimitive.Close
          className="calendar-popover-close"
          aria-label="Close event details"
        >
          <X size={16} />
        </PopoverPrimitive.Close>
        <div className="calendar-popover-heading">
          <div>
            <span className="calendar-popover-kicker">EVENT DETAILS</span>
            <h2>{event.title}</h2>
          </div>
        </div>
        <div className="calendar-popover-badges">
          <span>
            {STATUS_LABELS[resource.status] ||
              resource.status?.replaceAll("_", " ")}
          </span>
          {resource.priority && resource.priority !== "normal" && (
            <span>{resource.priority.toUpperCase()} priority</span>
          )}
        </div>
        <dl className="calendar-popover-meta">
          <div>
            <CalendarDays size={15} />
            <dt className="sr-only">Date</dt>
            <dd>
              {dateText}
              {endText}
            </dd>
          </div>
          <div>
            <Clock3 size={15} />
            <dt className="sr-only">Start time</dt>
            <dd>{timeText}</dd>
          </div>
          {resource.institution_name && (
            <div>
              <Building2 size={15} />
              <dt className="sr-only">Institution</dt>
              <dd>{resource.institution_name}</dd>
            </div>
          )}
          {resource.venue && (
            <div>
              <MapPin size={15} />
              <dt className="sr-only">Venue</dt>
              <dd>{resource.venue}</dd>
            </div>
          )}
        </dl>
        {resource.description && (
          <p className="calendar-popover-description">{resource.description}</p>
        )}
        {resource.requirements?.length > 0 && (
          <div className="calendar-popover-requirements">
            {resource.requirements.map((requirement) => (
              <span key={requirement}>{requirement.replaceAll("_", " ")}</span>
            ))}
          </div>
        )}
        <div className="calendar-popover-actions">
          <Link to={`/events/${resource.id}`}>
            View full details <ArrowUpRight size={16} />
          </Link>
          <PopoverPrimitive.Close asChild>
            <button onClick={() => openDay(event.start)}>
              Open day <CalendarDays size={15} />
            </button>
          </PopoverPrimitive.Close>
        </div>
      </PopoverContent>
    </Popover>
  );
}
